import React, { useEffect, useState, useMemo } from 'react';
import {
    Box, Typography, Grid, TextField, MenuItem, Paper,
    CircularProgress, IconButton, useMediaQuery, useTheme,
    Button, Table, TableBody, TableCell, TableContainer, TableHead,
    TableRow, Checkbox, Select,
    Container, Tooltip, Divider, InputAdornment, Accordion, AccordionSummary, AccordionDetails,
    Stack, Chip, TableFooter
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { motion } from "framer-motion";
import {
    FiBookOpen, FiArrowLeft, FiPlus, FiHash, FiLayers, FiBook, FiTag,
    FiCheckSquare, FiInfo, FiAlertCircle, FiSearch, FiClipboard,
    FiAward, FiClock, FiChevronLeft, FiEdit3, FiCheckCircle,
    FiChevronRight, FiX, FiCheck, FiFilter,
    FiFileText,
} from 'react-icons/fi';
import api from '../../api';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import { AiOutlineFilePdf, AiOutlineFileAdd } from "react-icons/ai";
import {
    useReactTable,
    getCoreRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    getFilteredRowModel,
    flexRender,
} from '@tanstack/react-table';
import jsPDF from 'jspdf';
import font from './NotoSansDevanagari-Regular-normal.js';
import marathiFont from "./NotoSansDevanagari-Regular-normal";
import 'jspdf-autotable';
import './QuestionSetPage.css';
import { UI, fadeSlideDown, pulseIcon, sheen, rowEnter, bounce } from './QuestionSetPageStyling.ts';
import './QuestionSetPageStyling.ts';
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import confetti from "canvas-confetti";




const QuestionSetPage = () => {
    const [pdfCount, setPdfCount] = useState('');
    const [exams, setExams] = useState([]);
    const [subjects, setSubjects] = useState([]);
    const [topics, setTopics] = useState([]);
    const [questions, setQuestions] = useState([]);
    const [questionSetName, setQuestionSetName] = useState('');
    const [totalMarks, setTotalMarks] = useState('');
    const [passingMarks, setPassingMarks] = useState('');
    const [selectedExam, setSelectedExam] = useState('');
    const [selectedSubject, setSelectedSubject] = useState('');
    const [selectedTopic, setSelectedTopic] = useState('');
    const [questionLimit, setQuestionLimit] = useState('');
    const [selectedQuestions, setSelectedQuestions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [columnFilters, setColumnFilters] = useState([]);
    const [sorting, setSorting] = useState([]);
    const [duration, setDuration] = useState('');

    const [selectedExamObj, setSelectedExamObj] = useState(null);
    const [selectedSubjectObj, setSelectedSubjectObj] = useState(null);
    const [selectedTopicObj, setSelectedTopicObj] = useState(null);
    const [schoolName, setSchoolName] = useState('Sunnydale International School');
    const [pdfTotalMarks, setPdfTotalMarks] = useState('');
    const [pdfDuration, setPdfDuration] = useState('');
    const [pdfPassingMarks, setPdfPassingMarks] = useState(10); // default value

    const theme = useTheme();
    const isSmall = useMediaQuery(theme.breakpoints.down('sm'));
    const navigate = useNavigate();

    const passingError = passingMarks && totalMarks && passingMarks > totalMarks;


    const [page, setPage] = useState(0); // current page index
    const rowsPerPage = 10;
    const totalQuestions = questions.length;

    const totalPages = Math.ceil(totalQuestions / rowsPerPage);
    const currentPage = page + 1;
    const [pdfUrl, setPdfUrl] = useState(null);

    // Table state
    const [rowSelection, setRowSelection] = useState({});

    const SortableHeader = ({ column, title }) => (
        <Box
            sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}
            onClick={column.getToggleSortingHandler()}
        >
            {title}
            {column.getIsSorted() === 'asc' ? ' 🔼' : column.getIsSorted() === 'desc' ? ' 🔽' : ''}
        </Box>
    );


    const columns = useMemo(() => [
        {
            id: 'select',
            header: ({ table }) => (
                <Checkbox
                    checked={table.getIsAllRowsSelected()}
                    onChange={table.getToggleAllRowsSelectedHandler()}
                />
            ),
            cell: ({ row, table }) => {
                const selected = row.getIsSelected();
                const selectedCount = Object.keys(table.getState().rowSelection).length;

                return (
                    <Checkbox
                        checked={selected}
                        onChange={(e) => {
                            if (!selected && questionLimit && selectedCount >= questionLimit) {
                                alert(`You can only select up to ${questionLimit} questions.`);
                                return;
                            }
                            row.getToggleSelectedHandler()(e);
                        }}
                    />
                );
            },
        },
        {
            accessorKey: 'id',
            header: ({ column }) => <SortableHeader column={column} title="Question ID" />,
            filterFn: 'includesString',
        },
        {
            accessorKey: 'question_text',
            header: ({ column }) => <SortableHeader column={column} title="Question" />,
            filterFn: 'includesString',
        },
        {
            accessorKey: 'option_a',
            header: ({ column }) => <SortableHeader column={column} title="Option A" />,
            filterFn: 'includesString',
        },
        {
            accessorKey: 'option_b',
            header: ({ column }) => <SortableHeader column={column} title="Option B" />,
            filterFn: 'includesString',
        },
        {
            accessorKey: 'option_c',
            header: ({ column }) => <SortableHeader column={column} title="Option C" />,
            filterFn: 'includesString',
        },
        {
            accessorKey: 'option_d',
            header: ({ column }) => <SortableHeader column={column} title="Option D" />,
            filterFn: 'includesString',
        },
        {
            accessorKey: 'correct_option',
            header: ({ column }) => <SortableHeader column={column} title="Correct Option" />,
            filterFn: 'includesString',
        },
    ], [questionLimit]);

    const table = useReactTable({
        data: questions,
        columns,
        state: {
            rowSelection,
            columnFilters,
            sorting,
        },
        onRowSelectionChange: (updater) => {
            const newSelection = typeof updater === 'function' ? updater(rowSelection) : updater;
            setRowSelection(newSelection);

            const selectedIds = Object.keys(newSelection)
                .map((rowId) => {
                    const index = parseInt(rowId, 10);
                    const row = table.getRowModel().rows[index];
                    return row?.original?.id;
                })
                .filter(Boolean);

            setSelectedQuestions(selectedIds);
        },
        onColumnFiltersChange: setColumnFilters,
        onSortingChange: setSorting,
        getCoreRowModel: getCoreRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
    });

    const pageIndex = table.getState().pagination.pageIndex;
    const pageSize = table.getState().pagination.pageSize;

    //fetch exams
    useEffect(() => {
        api.get('/exams')
            .then(res => setExams(res.data))
            .catch(err => console.error('Error fetching exams:', err));
    }, []);

    //fetch subjects
    useEffect(() => {
        if (selectedExam) {
            api.get(`/subjects?exam_id=${selectedExam}`)  // <-- REPLACED
                .then(res => setSubjects(res.data))
                .catch(err => console.error('Error fetching subjects:', err));
        } else {
            setSubjects([]);
        }
    }, [selectedExam]);

    //fetch topics
    useEffect(() => {
        if (selectedSubject) {
            api.get(`/topics?subject_id=${selectedSubject}`)  // <-- REPLACED
                .then(res => setTopics(res.data))
                .catch(err => console.error('Error fetching topics:', err));
        } else {
            setTopics([]);
        }
    }, [selectedSubject]);

    //fetch questions
    useEffect(() => {
        if (!selectedExam) {
            setQuestions([]);
            return;
        }

        setLoading(true);

        const params = new URLSearchParams();
        params.append('exam_id', selectedExam);
        if (selectedSubject) params.append('subject_id', selectedSubject);
        if (selectedTopic) params.append('topic_id', selectedTopic);

        api.get(`/questions?${params.toString()}`)  // <-- REPLACED
            .then(res => {
                const formattedQuestions = Array.isArray(res.data)
                    ? res.data.map((q) => ({
                        ...q,
                        id: Number(q.id || q.question_id || q._id),
                    }))
                    : [];
                setQuestions(formattedQuestions);
            })
            .catch(err => {
                console.error('Error fetching questions:', err);
                setQuestions([]);
            })
            .finally(() => setLoading(false));
    }, [selectedExam, selectedSubject, selectedTopic]);



    const handleCreateQuestionSet = async () => {
        // Validate input fields (exam required too)
        if (!selectedExam || !questionSetName.trim() || !totalMarks || !passingMarks || selectedQuestions.length === 0) {
            Swal.fire({
                icon: 'warning',
                title: 'Missing Fields',
                text: 'Please fill in all required fields (Exam, Name, Marks, and at least one question).',
            });
            return;
        }

        // Validate numeric fields
        const total = parseInt(totalMarks, 10);
        const passing = parseInt(passingMarks, 10);

        if (isNaN(total) || isNaN(passing) || total <= 0 || passing <= 0 || passing > total) {
            Swal.fire({
                icon: 'warning',
                title: 'Invalid Marks',
                text: 'Please ensure total and passing marks are valid. Passing marks should not exceed total marks.',
            });
            return;
        }

        setSubmitting(true);

        try {
            Swal.fire({
                title: '🚀 Creating Question Set...',
                html: 'Processing your request... <b></b>',
                timer: 2000,
                timerProgressBar: true,
                allowOutsideClick: false,
                didOpen: () => {
                    Swal.showLoading();
                    const b = Swal.getHtmlContainer().querySelector('b');
                    let timerInterval = setInterval(() => {
                        b.textContent = `${Swal.getTimerLeft()} ms`;
                    }, 100);
                },
            });

            const payload = {
                exam_id: selectedExam,
                name: questionSetName.trim(),
                total_marks: total,
                passing_marks: passing,
                question_ids: selectedQuestions.map(id => parseInt(id, 10)),
                question_set_type: "manual"
            };

            if (duration) {
                payload.duration_minutes = parseInt(duration, 10);
            }
            if (selectedSubject) {
                payload.subject_id = selectedSubject;
            }
            if (selectedTopic) {
                payload.topic_id = selectedTopic;
            }

            const response = await api.post('/practice-set-questions', payload);
            Swal.close();

            if (response.data?.success) {
                confetti({ particleCount: 150, spread: 70, origin: { y: 0.6 } });
                setTimeout(() => {
                    confetti({ particleCount: 100, spread: 120, origin: { y: 0.8 } });
                }, 500);

                Swal.fire({
                    icon: 'success',
                    title: `🎉 Question Set "${questionSetName}" Created!`,
                    html: `
            <p>Total Marks: <b>${total}</b></p>
            <p>Passing Marks: <b>${passing}</b></p>
            <p>Questions Added: <b>${selectedQuestions.length}</b></p>
        `,
                    showDenyButton: true,
                    showCloseButton: true,
                    showCancelButton: true,
                    confirmButtonText: "📄 View Sets",
                    denyButtonText: `
        <span style="display:flex; align-items:center; gap:6px; color:white; font-weight:500;">
            <svg xmlns="http://www.w3.org/2000/svg" 
                 fill="white" viewBox="0 0 24 24" width="18" height="18">
                <path d="M19 11h-6V5h-2v6H5v2h6v6h2v-6h6z"/>
            </svg>
            Create Another Set
        </span>
    `,
                    cancelButtonText: "Close",
                    confirmButtonColor: "#28a745",
                    denyButtonColor: "#007bff",
                    backdrop: `
            rgba(0,0,100,0.4)
            url("https://media.giphy.com/media/5GoVLqeAOo6PK/giphy.gif")
            bottom right
            no-repeat
        `,
                }).then((result) => {
                    if (result.isConfirmed) {
                        // 👉 Shortcut: Go to sets list (adjust navigation as needed)
                        navigate("/admin/manage-question-sets");
                    } else if (result.isDenied) {
                        // 👉 Shortcut: Reset form for new creation
                        setQuestionSetName('');
                        setTotalMarks('');
                        setPassingMarks('');
                        setSelectedQuestions([]);
                        setRowSelection({});
                    }
                });
            } else {
                Swal.fire({
                    icon: 'error',
                    title: 'Failed',
                    text: response.data?.message || 'Something went wrong.',
                });
            }
        } catch (error) {
            Swal.close();
            Swal.fire({
                icon: 'error',
                title: '❌ Submission Failed',
                text: error.response?.data?.message || error.message || 'An error occurred.',
                footer: '<a href="#">Try again or contact support</a>',
                showCancelButton: true,
                confirmButtonText: "🔄 Retry",
                cancelButtonText: "Close",
            }).then((result) => {
                if (result.isConfirmed) {
                    handleCreateQuestionSet(); // retry
                }
            });
        } finally {
            setSubmitting(false);
        }
    };

    //watermark fuction
    // const addWatermark = (doc, text) => {
    //     const pageCount = doc.internal.getNumberOfPages();
    //     const pageWidth = doc.internal.pageSize.getWidth();
    //     const pageHeight = doc.internal.pageSize.getHeight();

    //     for (let i = 1; i <= pageCount; i++) {
    //         doc.setPage(i);

    //         // Save graphics state for opacity
    //         doc.setGState(new doc.GState({ opacity: 0.1 })); // ✅ set transparency (0.0 = invisible, 1.0 = solid)

    //         // Big bold font
    //         doc.setFont('helvetica', 'bold');
    //         doc.setFontSize(50);

    //         const x = pageWidth / 2;
    //         const y = pageHeight / 2;

    //         // Draw text at center (horizontal + vertical)
    //         doc.setTextColor(100, 100, 100); // subtle gray
    //         doc.text(text, x, y, {
    //             align: 'center',
    //             angle: 30 // diagonal tilt for style
    //         });

    //         // Restore graphics state (so later text is normal)
    //         doc.setGState(new doc.GState({ opacity: 1 }));
    //         doc.setTextColor(0, 0, 0);
    //         doc.setFontSize(12);
    //     }
    // };

    //this function is for watermark text
    const addWatermark = (doc, text) => {
        const pageCount = doc.internal.getNumberOfPages();
        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();

        for (let i = 1; i <= pageCount; i++) {
            doc.setPage(i);

            // Semi-transparent effect
            doc.setGState(new doc.GState({ opacity: 0.15 }));

            doc.setFont('helvetica', 'bold');
            doc.setFontSize(45);

            const x = pageWidth / 2 + 10;  //shift watermark to right
            const y = pageHeight / 2;

            // Gradient colors (top → bottom)
            const gradientColors = [
                [255, 0, 0],     // red
                [255, 140, 0],   // orange
                [50, 205, 50],   // green
                [30, 144, 255],  // blue
                //[138, 43, 226],  // violet
            ];

            // Draw text multiple times with slightly shifted Y for gradient effect
            gradientColors.forEach((color, idx) => {
                doc.setTextColor(...color);
                doc.text(text, x, y + (idx * 1.2), {
                    align: 'center',
                    baseline: 'middle',
                    angle: 25  //reduced tilt to avopid cutting
                });
            });

            // Restore normal state
            doc.setGState(new doc.GState({ opacity: 1 }));
            doc.setTextColor(0, 0, 0);
            doc.setFontSize(12);
        }
    };

    const loadImage = (url) =>
        new Promise(resolve => {
            if (!url) return resolve(null);
            const img = new Image();
            img.crossOrigin = 'anonymous';
            img.onload = () => {
                const canvas = document.createElement('canvas');
                canvas.width = img.width;
                canvas.height = img.height;
                const ctx = canvas.getContext('2d');
                ctx.drawImage(img, 0, 0);
                resolve(canvas.toDataURL('image/png'));
            };
            img.onerror = () => resolve(null);
            img.src = url;
        });

    //function for watermark image
    const addImageWatermark = async (doc, imgUrl) => {
        return new Promise((resolve) => {
            const img = new Image();
            img.crossOrigin = 'anonymous';
            img.src = imgUrl;
            img.onload = () => {
                const pageCount = doc.internal.getNumberOfPages();
                for (let i = 1; i <= pageCount; i++) {
                    doc.setPage(i);

                    const pageWidth = doc.internal.pageSize.getWidth();
                    const pageHeight = doc.internal.pageSize.getHeight();

                    // Set transparency
                    doc.setGState(new doc.GState({ opacity: 0.1 }));

                    // Center watermark
                    const w = pageWidth * 0.6; // width of watermark
                    const h = (img.height / img.width) * w; // maintain aspect ratio
                    const x = (pageWidth - w) / 2;
                    const y = (pageHeight - h) / 2;

                    doc.addImage(img, 'PNG', x, y, w, h);

                    // Reset opacity
                    doc.setGState(new doc.GState({ opacity: 1 }));
                }
                resolve();
            };
            img.onerror = () => resolve(); // ignore if image fails
        });
    };






    const generateRandomPDF = async () => {
        const num = parseInt(pdfCount, 10);

        if (!num || num <= 0) {
            Swal.fire({
                icon: 'warning',
                title: '⚠️ Invalid Count',
                text: 'Please enter a valid number of questions.'
            });
            return;
        }

        if (!selectedExam) {
            Swal.fire({
                icon: 'warning',
                title: '🎯 Selection Missing',
                text: 'Please select an exam first.'
            });
            return;
        }

        if (num > 100) {
            const confirmLarge = await Swal.fire({
                icon: 'warning',
                title: 'Large PDF Warning',
                text: `You are generating ${num} questions. This may take several seconds. Continue?`,
                showCancelButton: true
            });
            if (!confirmLarge.isConfirmed) return;
        }

        const filtered = questions.filter(
            q =>
                q.exam_id === parseInt(selectedExam, 10) &&
                (!selectedSubject || q.subject_id === parseInt(selectedSubject, 10)) &&
                (!selectedTopic || q.topic_id === parseInt(selectedTopic, 10))
        );

        if (filtered.length === 0) {
            Swal.fire({
                icon: 'warning',
                title: 'No Questions Found 📭',
                text: 'No questions available for this selection.'
            });
            return;
        }

        try {
            Swal.fire({
                title: '📄 Generating PDF...',
                html: 'Please wait while we prepare your exam paper.<br/><br/><b>This may take a few moments for large exams.</b>',
                allowOutsideClick: false,
                didOpen: () => Swal.showLoading()
            });

            // ✅ Initialize jsPDF
            const doc = new jsPDF();

            // ✅ Register Marathi/Devanagari font
            doc.addFileToVFS("NotoSansDevanagari-Regular.ttf", marathiFont);
            doc.addFont("NotoSansDevanagari-Regular.ttf", "NotoSansDevanagari", "normal");
            doc.setFont("NotoSansDevanagari");

            // Shuffle questions
            const shuffled = [...filtered];
            for (let i = shuffled.length - 1; i > 0; i--) {
                const j = Math.floor(Math.random() * (i + 1));
                [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
            }
            let selected = shuffled.slice(0, num);

            // Handle repeated questions if marks exceed count
            const availableCount = selected.length;
            if (pdfTotalMarks > availableCount) {
                const allowRepeated = await Swal.fire({
                    icon: 'question',
                    title: '⚠ Total Marks Exceed Questions',
                    html: `You have requested <b>${pdfTotalMarks}</b> marks but only <b>${availableCount}</b> questions are available.<br/><br/>Do you want to allow repeated questions to fill the marks?`,
                    showDenyButton: true,
                    confirmButtonText: '✅ Allow Repeats',
                    denyButtonText: '❌ No Repeats',
                    allowOutsideClick: false,
                    background: '#fff3e0',
                    confirmButtonColor: '#28a745',
                    denyButtonColor: '#dc3545',
                    customClass: {
                        title: 'swal2-title-bold',
                        htmlContainer: 'swal2-html-modern'
                    }
                });

                if (allowRepeated.isConfirmed) {
                    const repeatCount = Math.ceil(pdfTotalMarks / availableCount);
                    const repeatedQuestions = [];
                    for (let i = 0; i < repeatCount; i++) repeatedQuestions.push(...selected);
                    selected = repeatedQuestions.slice(0, pdfTotalMarks);
                } else if (allowRepeated.isDenied) {
                    Swal.fire({
                        icon: 'error',
                        title: '❌ Cannot Generate PDF',
                        text: `Please change your question count or marks so they match.`,
                        confirmButtonColor: '#dc3545'
                    });
                    return;
                }
            }

            const fileName = `${selectedExamObj?.title || 'Exam'}_${selectedSubjectObj?.name || 'Subject'}_${selectedTopicObj?.title || 'Topic'}_${num}_questions.pdf`;

            const practiceSetResponse = await api.post('/practice-set-questions', {
                exam_id: selectedExam,
                subject_id: selectedSubject || null,
                topic_id: selectedTopic || null,
                name: fileName,
                total_marks: pdfTotalMarks,
                passing_marks: pdfPassingMarks,
                duration_minutes: pdfDuration,
                question_ids: selected.map(q => q.id),
                question_set_type: "auto"
            });

            const practiceSetData = practiceSetResponse.data;
            const practiceSetId = practiceSetData.practice_set_id;

            const loadImage = (url) =>
                new Promise(resolve => {
                    if (!url) return resolve(null);
                    const img = new Image();
                    img.crossOrigin = 'anonymous';
                    img.onload = () => {
                        const canvas = document.createElement('canvas');
                        canvas.width = img.width;
                        canvas.height = img.height;
                        const ctx = canvas.getContext('2d');
                        ctx.drawImage(img, 0, 0);
                        resolve(canvas.toDataURL('image/png'));
                    };
                    img.onerror = () => resolve(null);
                    img.src = url;
                });


         const addHeader = async (doc, y) => {
    const logoData = await loadImage('https://marketplace.canva.com/EAGUe-CYFgU/1/0/1600w/canva-grey-orange-modern-circle-class-logo-Aro9ea9TDP4.jpg');
    if (logoData) doc.addImage(logoData, 'PNG', 10, y, 20, 20);

    doc.setFontSize(14);
    const textY = y + 12;
    doc.text(schoolName || '', 35, textY);
    doc.text('Date: __________', 150, textY);

    // --- Horizontal line immediately below school name/date ---
     const topMargin = 10; // extra space between text/logo and line
    const lineY = textY + topMargin;
    doc.setLineWidth(0.3); // thin line
    doc.line(10, lineY, 200, lineY);

    y = lineY + 8; // set y for the next content, with spacing

    // Exam details
    doc.setFontSize(13);
    doc.text(`Exam: ${selectedExamObj?.title || ''}`, 10, y);
    doc.text(`Subject: ${selectedSubjectObj?.name || 'N/A'}`, 150, y);
    y += 8;
    doc.text(`Topic: ${selectedTopicObj?.title || 'N/A'}`, 10, y);
    doc.text(`Total Marks: ${pdfTotalMarks || ''}`, 150, y);
    y += 8;
    doc.text(`Duration: ${pdfDuration || ''} mins`, 10, y);
    y += 8;

    // Student info
    doc.setFontSize(12);
    doc.text('Student Name: _______________________', 10, y);
    doc.text('Roll No: __________', 150, y);
    y += 10;

    // --- Instructions Box ---
    const boxX = 10;
    const boxWidth = 190;
    const boxStartY = y;
    const instructions = [
        '- Answer all questions.',
        '- Use blue/black ink only.',
        '- Each question carries equal marks.'
    ];
    const lineSpacing = 6;

    // Draw thin-line rectangle
    doc.setLineWidth(0.2); // thin border
    const boxHeight = instructions.length * lineSpacing + 15; // adjust height
    doc.rect(boxX, boxStartY, boxWidth, boxHeight); // thin box

    // Draw instructions text inside the box
    doc.setFontSize(11);
    doc.text('Instructions:', boxX + 5, boxStartY + 10);
    doc.setFontSize(10);
    instructions.forEach((line, index) => {
        doc.text(line, boxX + 10, boxStartY + 15 + index * lineSpacing);
    });

    y += boxHeight + 5; // move y below the box
    doc.line(10, y, 200, y); // horizontal line after instructions
    return y + 10;
};



            const addFooter = (doc, footerText) => {
                const pageCount = doc.internal.getNumberOfPages();
                for (let i = 1; i <= pageCount; i++) {
                    doc.setPage(i);
                    doc.setFontSize(10);
                    doc.line(10, 285, 200, 285);
                    doc.text(footerText || '', 105, 290, { align: 'center' });
                }
            };

            addFooter(doc, schoolName);

            // ---------------- COLUMN LAYOUT SETUP ----------------
            const margin = 10;
            const columnGap = 10;
            const pageWidth = doc.internal.pageSize.getWidth();
            const pageHeight = doc.internal.pageSize.getHeight();
            const columnWidth = (pageWidth - (2 * margin) - columnGap) / 2;

            // Spacing constants
            const lineHeight = 7.5;          // line height for text
            const labelXOffset = 8;           // A/B/C/D label
            const optionTextXOffset = 15;     // distance from label to option text
            const gapAfterQuestion = 5;       // space between question text and first option
            const gapAfterOption = 3;         // space after each option
            const questionImageHeight = 40;   // fixed height for question image
            const optionImageHeight = 30;     // fixed height for option image
            const footerHeight = 15;          // NEW: space reserved for footer
            const bottomMargin = margin + footerHeight; // NEW: prevents content going into footer

            let leftY = await addHeader(doc, margin); // header only on 1st page
            let rightY = leftY;
            let onLeft = true;

            // Draw divider on first page
            // doc.line(pageWidth / 2, margin, pageWidth / 2, pageHeight - margin);
            // Draw vertical divider below header
            doc.line(pageWidth / 2, leftY, pageWidth / 2, pageHeight - margin);


            for (let i = 0; i < selected.length; i++) {
                const q = selected[i];

                // Update progress every 5 questions
                if (i % 5 === 0) {
                    Swal.update({
                        title: '📄 Generating PDF...',
                        html: `Processing question <b>${i + 1}</b> of <b>${selected.length}</b>...`
                    });
                }

                let currentY = onLeft ? leftY : rightY;

                // Check if current column exceeds page height
                if (currentY + 50 > pageHeight - margin) {
                    if (onLeft) {
                        onLeft = false; // switch to right column
                        currentY = rightY;
                    } else {
                        // new page
                        doc.addPage();
                        currentY = margin;
                        leftY = currentY;
                        rightY = currentY;
                        onLeft = true;

                        // Draw divider line on every new page
                        doc.line(pageWidth / 2, margin, pageWidth / 2, pageHeight - margin);
                    }
                }

                const colX = onLeft ? margin : margin + columnWidth + columnGap;

                // Question number
                doc.setFontSize(12).setFont('NotoSansDevanagari', 'bold');
                doc.text(`${i + 1}.`, colX, currentY);

                // Question text
                doc.setFont('NotoSansDevanagari', 'normal');
                const wrappedText = doc.splitTextToSize(q.question_text || '', columnWidth - 10);
                doc.text(wrappedText, colX + 8, currentY);

                // Get exact height of wrapped text
                let textHeight = 0;
                for (let line of wrappedText) {
                    textHeight += doc.getTextDimensions(line).h;
                }
                currentY += textHeight + gapAfterQuestion;


                // Question image (if any)
                const qImage = await loadImage(q.question_image);
                if (qImage) {
                    doc.addImage(qImage, 'PNG', colX + 5, currentY, columnWidth - 20, questionImageHeight);
                    currentY += questionImageHeight + gapAfterQuestion;
                }

                // Options
                const options = ['a', 'b', 'c', 'd'];
                for (let opt of options) {
                    const label = `option_${opt}`;
                    const imageLabel = `option_${opt}_image`;

                    // ---------------- NEW: Draw radio button ----------------
                    const radioRadius = 2.5;          // small circle
                    const radioX = colX + labelXOffset - 5; // slightly left of A/B/C/D label
                    const radioY = currentY - 1;      // vertically aligned with text
                    doc.circle(radioX, radioY, radioRadius, 'S'); // 'S' = stroke only (empty circle)

                    // Option label (A/B/C/D)
                    doc.setFont('NotoSansDevanagari', 'bold');
                    doc.text(`${opt.toUpperCase()})`, colX + labelXOffset, currentY);

                    // Option text
                    const wrappedOpt = doc.splitTextToSize(q[label] || '', columnWidth - optionTextXOffset);
                    doc.setFont('NotoSansDevanagari', 'normal');
                    doc.text(wrappedOpt, colX + optionTextXOffset, currentY);
                    currentY += wrappedOpt.length * lineHeight;

                    // Option image (if any)
                    const img = await loadImage(q[imageLabel]);
                    if (img) {
                        doc.addImage(img, 'PNG', colX + optionTextXOffset, currentY, columnWidth - 30, optionImageHeight);
                        currentY += optionImageHeight + gapAfterOption;
                    } else {
                        currentY += gapAfterOption;
                    }
                }

                // Small gap after each question
                currentY += gapAfterQuestion;

                // Save Y position back to correct column
                if (onLeft) leftY = currentY;
                else rightY = currentY;
            }


            const pdfBlob = doc.output("blob");
            const blobUrl = URL.createObjectURL(pdfBlob);

            Swal.fire({
                icon: 'success',
                title: '🎉 PDF Ready!',
                html: `
            <p>Your exam paper <b>${fileName}</b> has been generated successfully.</p>
            <p>📂 You can find it inside your <b>Downloads</b> folder.</p>
            <br/>
            <button id="previewBtn" class="swal2-confirm swal2-styled">👀 Preview PDF</button>
            <button id="downloadBtn" class="swal2-cancel swal2-styled">⬇️ Download Again</button>
        `,
                showConfirmButton: false,
                showCloseButton: true,
                didOpen: () => {
                    document.getElementById("previewBtn").addEventListener("click", () =>
                        window.open(blobUrl, "_blank")
                    );
                    document.getElementById("downloadBtn").addEventListener("click", () => {
                        const link = document.createElement("a");
                        link.href = blobUrl;
                        link.download = fileName;
                        link.click();
                    });

                    const duration = 2000;
                    const end = Date.now() + duration;
                    const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 9999 };
                    const interval = setInterval(() => {
                        const timeLeft = end - Date.now();
                        if (timeLeft <= 0) return clearInterval(interval);
                        confetti({
                            ...defaults,
                            particleCount: 5 + Math.random() * 5,
                            origin: { x: Math.random(), y: Math.random() - 0.2 }
                        });
                    }, 250);
                }
            });
            //addWatermark(doc, schoolName || 'Confidential'); // you can pass your watermark text
            // addWatermark(doc, "Ambitious Career Academy");  //watermark text

            await addImageWatermark(doc, 'https://upload.wikimedia.org/wikipedia/commons/4/47/PNG_transparency_demonstration_1.png');

            doc.save(fileName);

        } catch (error) {
            console.error(error);
            Swal.fire({
                icon: 'error',
                title: '❌ Something went wrong!',
                text: error.message,
                confirmButtonColor: '#dc3545'
            });
        }
    };


    // Apply Filters function
    const applyFilters = () => {
        let filtered = questions;

        if (selectedExamObj) {
            filtered = filtered.filter((q) => q.exam_id === selectedExamObj.id);
        }
        if (selectedSubjectObj) {
            filtered = filtered.filter((q) => q.subject_id === selectedSubjectObj.id);
        }
        if (selectedTopicObj) {
            filtered = filtered.filter((q) => q.topic_id === selectedTopicObj.id);
        }
        if (questionLimit) {
            filtered = filtered.slice(0, questionLimit);
        }

        setSelectedQuestions(filtered);

        toast.success(`Filters applied! Showing ${filtered.length} questions.`, {
            position: "top-right",
            autoClose: 2500,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            theme: "colored",
        });
    };

    // Clear Filters function
    const clearFilters = () => {
        setSelectedExam("");
        setSelectedSubject("");
        setSelectedTopic("");
        setQuestionLimit("");
        setSelectedExamObj(null);
        setSelectedSubjectObj(null);
        setSelectedTopicObj(null);
        setSelectedQuestions(questions); // reset to full list
    };


    return (
        <Box p={2}>
            {/* 🔷 Header Placeholder */}
            <Box
                mb={3}
                display="flex"
                flexDirection={isSmall ? 'column' : 'row'}
                justifyContent="space-between"
                alignItems="center"
                gap={2}
                textAlign={isSmall ? 'center' : 'left'}
                //this sx code is for background of header
                sx={{
                    background: 'linear-gradient(90deg, #f8fbff, #e3f2fd)',
                    borderRadius: '12px',
                    padding: '16px 24px',
                    boxShadow: '0 4px 10px rgba(0,0,0,0.08)',
                    animation: 'fadeSlideDown 0.6s ease-out',
                }}
            >
                <IconButton
                    onClick={() => navigate(-1)}
                    sx={{
                        borderRadius: '12px',
                        p: '8px 12px',
                        background: 'linear-gradient(135deg, #00bcd4, #007bff)',
                        color: '#fff',
                        '&:hover': {
                            background: 'linear-gradient(135deg, #007bff, #00bcd4)',
                            transform: 'scale(1.05)',
                            boxShadow: '0 5px 12px rgba(0, 123, 255, 0.4)',
                        },
                        transition: 'all 0.25s ease',
                    }}
                >
                    <FiArrowLeft />
                    {!isSmall && <Typography variant="button" sx={{ ml: 1, fontWeight: 600 }}>Back</Typography>}
                </IconButton>

                <Box display="flex" alignItems="center"
                    justifyContent="center" gap={1} flex={1}>
                    <Box sx={{
                        fontSize: '2rem',
                        background: '#e3f2fd',
                        borderRadius: '50%',
                        p: 1,
                        color: '#1976d2',
                        animation: 'pulseIcon 1.6s infinite',
                    }}>
                        <FiCheckSquare />
                    </Box>
                    <Typography
                        variant={isSmall ? 'h6' : 'h5'}
                        sx={{
                            fontWeight: 'bold',
                            background: 'linear-gradient(90deg, #007BFF, #00d4ff)',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            textTransform: 'uppercase',
                            letterSpacing: '1.2px',
                            px: 1,
                            animation: 'fadeIn 1s ease-in',
                        }}
                    >
                        Create Questions Set
                    </Typography>
                </Box>
            </Box>



            {/* code for dropdwon section of exam subject and topic and no.of questions */}
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
            >
                <Paper
                    elevation={4}
                    sx={{
                        p: 2,
                        mb: 3,
                        borderRadius: 3,
                        background: "#fff",
                        boxShadow: "inset 0 1px 3px rgba(0,0,0,0.05), 0 8px 24px rgba(0,0,0,0.08)",
                        transition: "transform 0.3s ease, box-shadow 0.3s ease",
                    }}
                >
                    <Accordion defaultExpanded disableGutters elevation={0} sx={{ bgcolor: "transparent" }}>
                        <AccordionSummary
                            expandIcon={
                                <motion.div
                                    whileHover={{ rotate: 20 }}
                                    whileTap={{ scale: 0.9 }}
                                    transition={{ type: "spring", stiffness: 300 }}
                                >
                                    <ExpandMoreIcon style={{ color: "#1976d2" }} />
                                </motion.div>
                            }
                            sx={{
                                "&:hover": {
                                    backgroundColor: "rgba(25,118,210,0.05)",
                                    borderRadius: 2,
                                },
                                borderBottom: "1px solid #f0f0f0",
                            }}
                        >
                            <Box>
                                <Box display="flex" alignItems="center" mb={1.5}>
                                    <motion.div whileHover={{ rotate: 15, scale: 1.15 }}>
                                        <FiFilter style={{ fontSize: 28, color: "#1976d2", marginRight: 8 }} />
                                    </motion.div>
                                    <Typography
                                        variant="h5"
                                        sx={{
                                            fontWeight: "bold",
                                            background: "linear-gradient(90deg, #1976d2, #42a5f5)",
                                            WebkitBackgroundClip: "text",
                                            WebkitTextFillColor: "transparent",
                                            position: "relative",
                                        }}
                                    >
                                        Filters
                                    </Typography>
                                </Box>

                                <Typography variant="subtitle2" color="textSecondary" mb={3}>
                                    Apply filters to refine your questions list.
                                </Typography>



                                <Box mt={0.5} display="flex" gap={1} flexWrap="wrap">
                                    {[{
                                        obj: selectedExamObj,
                                        label: "Exam",
                                        icon: <FiLayers />
                                    }, {
                                        obj: selectedSubjectObj,
                                        label: "Subject",
                                        icon: <FiBook />
                                    }, {
                                        obj: selectedTopicObj,
                                        label: "Topic",
                                        icon: <FiTag />
                                    }].map((item, index) => (
                                        <motion.div
                                            whileHover={{ scale: 1.05 }}
                                            whileTap={{ scale: 0.95 }}
                                            initial={{ opacity: 0, y: 10 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            exit={{ opacity: 0, scale: 0.8 }}
                                            key={index}
                                            style={{ display: "inline-block" }}
                                        >
                                            <Chip
                                                size="small"
                                                icon={item.obj ? item.icon : null}
                                                label={item.obj ? item.obj.title || item.obj.name : `${item.label}: Any`}
                                                sx={{
                                                    minWidth: 200,
                                                    backgroundColor: item.obj ? "#e3f2fd" : "#f5f5f5",
                                                    fontWeight: "bold",
                                                    cursor: "pointer",
                                                    transition: "all 0.3s ease",
                                                    boxShadow: "0 2px 6px rgba(25,118,210,0.12)",
                                                    "&:hover": {
                                                        backgroundColor: "#e3f2fd",
                                                        transform: "translateY(-2px)",
                                                        boxShadow: "0 6px 14px rgba(25,118,210,0.2)",
                                                    },


                                                }}
                                            />
                                        </motion.div>
                                    ))}
                                    {questionLimit && (
                                        <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                                            <Chip
                                                size="small"
                                                icon={<FiHash />}
                                                label={`${questionLimit} Qs`}
                                                // sx={{ backgroundColor: "#e3f2fd", fontWeight: "bold" }}
                                                sx={{
                                                    backgroundColor: "#e3f2fd",
                                                    fontWeight: "bold",
                                                    cursor: "pointer",
                                                    transition: "all 0.3s ease",
                                                    boxShadow: "0 2px 6px rgba(25,118,210,0.12)",
                                                    "&:hover": {
                                                        transform: "translateY(-2px)",
                                                        boxShadow: "0 6px 14px rgba(25,118,210,0.2)",
                                                    },
                                                }}
                                            />
                                        </motion.div>
                                    )}
                                </Box>
                            </Box>
                        </AccordionSummary>

                        <AccordionDetails>
                            <Grid container spacing={2} direction={{ xs: "column", md: "row" }} alignItems="stretch">
                                {/* Exam */}
                                <Grid item xs={12} md="auto" sx={{ flexGrow: 1 }}>
                                    <Box
                                        sx={{
                                            p: 1,
                                            borderRadius: 2,
                                            transition: "all 0.3s ease",
                                            "&:hover": {
                                                backgroundColor: "#f7fbff",
                                                transform: "translateY(-2px)",
                                                boxShadow: "0 6px 16px rgba(25,118,210,0.12)",
                                            },
                                        }}
                                    >
                                        <TextField
                                            select
                                            fullWidth
                                            size="small"
                                            label="Select Exam (Required)"
                                            value={selectedExam}
                                            onChange={(e) => {
                                                const id = e.target.value;
                                                setSelectedExam(id);
                                                setSelectedSubject("");
                                                setSelectedTopic("");
                                                const ex = exams.find((x) => x.id === id);
                                                setSelectedExamObj(ex || null);
                                            }}
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <FiLayers style={{ color: "#1976d2" }} />
                                                    </InputAdornment>
                                                ),
                                            }}
                                            sx={{
                                                minWidth: 220,
                                                "& .MuiOutlinedInput-root": {
                                                    borderRadius: 2,
                                                    backgroundColor: "#fafafa",
                                                    transition: "all 0.3s ease",
                                                    "&.Mui-focused": {
                                                        boxShadow: "0 0 0 3px rgba(25,118,210,0.2)",
                                                        backgroundColor: "#fff",
                                                    },
                                                },
                                            }}
                                        >
                                            <MenuItem value="">None</MenuItem>
                                            {exams.map((ex) => (
                                                <MenuItem key={ex.id} value={ex.id}>
                                                    {ex.title}
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                    </Box>
                                </Grid>


                                {/* Subject */}
                                <Grid item xs={12} md="auto" sx={{ flexGrow: 1 }}>
                                    <Box
                                        sx={{
                                            p: 1,
                                            borderRadius: 2,
                                            transition: "all 0.3s ease",
                                            "&:hover": {
                                                backgroundColor: "#f7fbff",
                                                transform: "translateY(-2px)",
                                                boxShadow: "0 6px 16px rgba(25,118,210,0.12)",
                                            },
                                        }}
                                    >
                                        <TextField
                                            select
                                            fullWidth
                                            size="small"
                                            label="Select Subject (Optional)"
                                            value={selectedSubject}
                                            onChange={(e) => {
                                                const id = e.target.value;
                                                setSelectedSubject(id);
                                                setSelectedSubjectObj(subjects.find((s) => s.id === id) || null);
                                            }}
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <FiBook style={{ color: "#1976d2" }} />
                                                    </InputAdornment>
                                                ),
                                            }}
                                            sx={{
                                                minWidth: 200,
                                                "& .MuiOutlinedInput-root": {
                                                    borderRadius: 2,
                                                    backgroundColor: "#fafafa",
                                                    transition: "all 0.3s ease",
                                                    "&.Mui-focused": {
                                                        boxShadow: "0 0 0 3px rgba(25,118,210,0.2)",
                                                        backgroundColor: "#fff",
                                                    },
                                                },
                                            }}
                                        >
                                            <MenuItem value="">None</MenuItem>
                                            {subjects.map((s) => (
                                                <MenuItem key={s.id} value={s.id}>
                                                    {s.name}
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                    </Box>
                                </Grid>

                                {/* Topic */}
                                <Grid item xs={12} md="auto" sx={{ flexGrow: 1 }}>
                                    <Box
                                        sx={{
                                            p: 1,
                                            borderRadius: 2,
                                            transition: "all 0.3s ease",
                                            "&:hover": {
                                                backgroundColor: "#f7fbff",
                                                transform: "translateY(-2px)",
                                                boxShadow: "0 6px 16px rgba(25,118,210,0.12)",
                                            },
                                        }}
                                    >
                                        <TextField
                                            select
                                            fullWidth
                                            size="small"
                                            label="Select Topic (Optional)"
                                            value={selectedTopic}
                                            onChange={(e) => {
                                                const id = e.target.value;
                                                setSelectedTopic(id);
                                                setSelectedTopicObj(topics.find((t) => t.id === id) || null);
                                            }}
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <FiTag style={{ color: "#1976d2" }} />
                                                    </InputAdornment>
                                                ),
                                            }}
                                            sx={{
                                                minWidth: 200,
                                                "& .MuiOutlinedInput-root": {
                                                    borderRadius: 2,
                                                    backgroundColor: "#fafafa",
                                                    transition: "all 0.3s ease",
                                                    "&.Mui-focused": {
                                                        boxShadow: "0 0 0 3px rgba(25,118,210,0.2)",
                                                        backgroundColor: "#fff",
                                                    },
                                                },
                                            }}
                                        >
                                            <MenuItem value="">None</MenuItem>
                                            {topics.map((t) => (
                                                <MenuItem key={t.id} value={t.id}>
                                                    {t.title}
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                    </Box>
                                </Grid>


                                {/* Question Limit */}
                                <Grid item xs={12} md="auto" sx={{ flexGrow: 1 }}>
                                    <Box
                                        sx={{
                                            p: 1,
                                            borderRadius: 2,
                                            transition: "all 0.3s ease",
                                            "&:hover": {
                                                backgroundColor: "#f7fbff",
                                                transform: "translateY(-2px)",
                                                boxShadow: "0 6px 16px rgba(25,118,210,0.12)",
                                            },
                                        }}
                                    >
                                        <TextField
                                            type="number"
                                            fullWidth
                                            size="small"
                                            label="No. of Questions"
                                            value={questionLimit}
                                            onChange={(e) => {
                                                const v = parseInt(e.target.value, 10);
                                                if (!isNaN(v) && v > 0) {
                                                    setQuestionLimit(v);
                                                    setSelectedQuestions([]);
                                                }
                                            }}
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <FiHash style={{ color: "#1976d2" }} />
                                                    </InputAdornment>
                                                ),
                                            }}
                                            sx={{
                                                minWidth: 180,
                                                "& .MuiOutlinedInput-root": {
                                                    borderRadius: 2,
                                                    backgroundColor: "#fafafa",
                                                    transition: "all 0.3s ease",
                                                    "&.Mui-focused": {
                                                        boxShadow: "0 0 0 3px rgba(25,118,210,0.2)",
                                                        backgroundColor: "#fff",
                                                    },
                                                },
                                            }}
                                        />
                                    </Box>
                                </Grid>

                            </Grid>
                        </AccordionDetails>
                    </Accordion>

                    {/* Sticky Buttons */}
                    <Box
                        display="flex"
                        justifyContent="flex-end"
                        gap={2}
                        mt={2}
                        sx={{
                            position: "sticky",
                            bottom: 0,
                            zIndex: 10,
                            backdropFilter: "blur(10px)",
                            background: "rgba(255, 255, 255, 0.8)",
                            p: 2,
                            borderTop: "1px solid rgba(255,255,255,0.3)",
                            boxShadow: "0 -4px 20px rgba(0,0,0,0.08)",
                            borderRadius: "0 0 12px 12px",
                        }}
                    >
                        {/* Clear Filters */}
                        <Tooltip title="Clear all selected filters" arrow>
                            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                                <Button
                                    variant="outlined"
                                    startIcon={<FiX />}
                                    onClick={clearFilters}
                                    sx={{
                                        borderRadius: 2,
                                        borderColor: "#1976d2",
                                        color: "#1976d2",
                                        fontWeight: "bold",
                                        textTransform: "uppercase",
                                        "&:hover": { backgroundColor: "rgba(25,118,210,0.08)", borderColor: "#1976d2" },
                                    }}
                                >
                                    Clear Filters
                                </Button>
                            </motion.div>
                        </Tooltip>

                        {/* Apply Filters */}
                        <Tooltip title="Apply selected filters" arrow>
                            <motion.div whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                animate={{ y: [0, -2, 0] }}
                                transition={{ repeat: Infinity, duration: 3, ease: "easeInOut" }}
                            >
                                <Button
                                    variant="contained"
                                    startIcon={<FiCheck />}
                                    onClick={applyFilters}
                                    sx={{
                                        borderRadius: 2,
                                        background: "linear-gradient(90deg, #1976d2, #42a5f5)",
                                        fontWeight: "bold",
                                        textTransform: "uppercase",
                                        boxShadow: "0 4px 14px rgba(25, 118, 210, 0.4)",
                                        "&:hover": {
                                            background: "linear-gradient(90deg, #1565c0, #2196f3)",
                                            transform: "translateY(-2px)",
                                            boxShadow: "0 6px 18px rgba(25, 118, 210, 0.6)",
                                        },
                                        animation: "pulse 2s infinite",
                                        "@keyframes pulse": {
                                            "0%": { boxShadow: "0 0 0 0 rgba(25,118,210,0.6)" },
                                            "70%": { boxShadow: "0 0 0 10px rgba(25,118,210,0)" },
                                            "100%": { boxShadow: "0 0 0 0 rgba(25,118,210,0)" },
                                        }
                                    }}
                                >
                                    Apply Filters
                                </Button>
                            </motion.div>
                        </Tooltip>
                    </Box>
                </Paper>
            </motion.div>

            {/* Toast container */}
            {/* <ToastContainer /> */}
            <ToastContainer
                position="top-right"
                autoClose={2000}
                hideProgressBar
                closeOnClick
                pauseOnHover
                draggable
                toastStyle={{
                    background: "linear-gradient(90deg, #1976d2, #42a5f5)",
                    color: "#fff",
                    fontWeight: "bold",
                    borderRadius: "8px",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
                }}
            />



            {/* adding input field and generate pdf button */}
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
            >
                <Paper
                    elevation={4}
                    sx={{
                        p: 3,
                        mb: 3,
                        borderRadius: 3,
                        background: "#fff",
                        boxShadow: "inset 0 1px 3px rgba(0,0,0,0.05), 0 8px 24px rgba(0,0,0,0.08)",
                        transition: "transform 0.3s ease, box-shadow 0.3s ease",
                        "&:hover": {
                            transform: "translateY(-2px) scale(1.01)",
                            boxShadow: "0 16px 36px rgba(0,0,0,0.15)"
                        }
                    }}
                >
                    {/* Heading */}
                    <Box display="flex" alignItems="center" mb={4}>
                        <motion.div whileHover={{ rotate: 15, scale: 1.1 }}>
                            <AiOutlineFilePdf style={{ fontSize: 28, color: "#1976d2", marginRight: 8 }} />
                        </motion.div>
                        <Typography
                            variant="h5"
                            sx={{ fontWeight: "bold", color: "#1976d2", position: "relative" }}
                        >
                            PDF Settings
                        </Typography>
                    </Box>
                    <Typography variant="subtitle2" color="textSecondary" mb={3}>
                        Enter details to generate your PDF question set.
                    </Typography>
                    <Divider sx={{ mb: 3 }} />

                    {/* Form Fields */}
                    <Grid container spacing={2} alignItems="stretch">
                        <Grid item xs={12} md={3} sm={6}>
                            <TextField
                                variant="outlined"
                                fullWidth
                                label={
                                    <span>
                                        Questions Count <span style={{ color: 'red' }}>*</span>
                                    </span>
                                }
                                type="number"
                                value={pdfCount}
                                onChange={(e) => setPdfCount(e.target.value)}
                                size="small"
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <FiHash color="#1976d2" />
                                        </InputAdornment>
                                    ),
                                    sx: { borderRadius: 2, backgroundColor: "#fafafa" }
                                }}
                                sx={{
                                    "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                        borderColor: "#1976d2",
                                        boxShadow: "0 0 6px rgba(25, 118, 210, 0.3)"
                                    }
                                }}
                            />
                        </Grid>

                        <Grid item xs={12} md={3} sm={6}>
                            <TextField
                                variant="outlined"
                                fullWidth
                                label={<span>Total Marks <span style={{ color: 'red' }}>*</span></span>}
                                type="number"
                                value={pdfTotalMarks}
                                onChange={(e) => setPdfTotalMarks(e.target.value)}
                                size="small"
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <FiAward color="#1976d2" />
                                        </InputAdornment>
                                    ),
                                    sx: { borderRadius: 2, backgroundColor: "#fafafa" }
                                }}
                                sx={{
                                    "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                        borderColor: "#1976d2",
                                        boxShadow: "0 0 6px rgba(25, 118, 210, 0.3)"
                                    }
                                }}
                            />
                        </Grid>

                        <Grid item xs={12} md={3} sm={6}>
                            <TextField
                                variant="outlined"
                                fullWidth
                                label={<span>Passing Marks <span style={{ color: 'red' }}>*</span></span>}
                                type="number"
                                value={pdfPassingMarks}
                                onChange={(e) => setPdfPassingMarks(e.target.value)}
                                size="small"
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <FiCheckCircle color="#1976d2" />
                                        </InputAdornment>
                                    ),
                                    sx: { borderRadius: 2, backgroundColor: "#fafafa" }
                                }}
                                sx={{
                                    "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                        borderColor: "#1976d2",
                                        boxShadow: "0 0 6px rgba(25, 118, 210, 0.3)"
                                    }
                                }}
                            />
                        </Grid>


                        <Grid item xs={12} md={3} sm={6}>
                            <TextField
                                variant="outlined"
                                fullWidth
                                label={<span>Duration (mins) <span style={{ color: 'red' }}>*</span></span>}
                                type="number"
                                value={pdfDuration}
                                onChange={(e) => setPdfDuration(e.target.value)}
                                size="small"
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <FiClock color="#1976d2" />
                                        </InputAdornment>
                                    ),
                                    sx: { borderRadius: 2, backgroundColor: "#fafafa" }
                                }}
                                sx={{
                                    "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                        borderColor: "#1976d2",
                                        boxShadow: "0 0 6px rgba(25, 118, 210, 0.3)"
                                    }
                                }}
                            />
                        </Grid>

                        <Grid item xs={12} md="auto" sm={6}>
                            <Tooltip title="Create a random set & export PDF" arrow>
                                <Button
                                    startIcon={<AiOutlineFilePdf />}
                                    variant="contained"
                                    onClick={generateRandomPDF}
                                    sx={{
                                        height: 40,
                                        px: 3,
                                        borderRadius: 2,
                                        background: "linear-gradient(90deg, #1976d2, #42a5f5)",
                                        textTransform: "uppercase",
                                        fontWeight: "bold",
                                        boxShadow: "0 4px 14px rgba(25, 118, 210, 0.4)",
                                        transition: "0.3s",
                                        "&:hover": {
                                            background: "linear-gradient(90deg, #1565c0, #2196f3)",
                                            transform: "translateY(-2px)",
                                            boxShadow: "0 6px 18px rgba(25, 118, 210, 0.6)"
                                        }
                                    }}
                                >
                                    Generate PDF
                                </Button>
                            </Tooltip>
                        </Grid>
                    </Grid>
                </Paper>
            </motion.div>



            {/* 📋 Question Table */}
            <Paper elevation={4} sx={{ p: 2, mb: 3 }}>

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                >
                    <Paper
                        elevation={4}
                        sx={{
                            p: 2,
                            mb: 3,
                            borderRadius: 3,
                            background: "#fff",
                            boxShadow: "inset 0 1px 3px rgba(0,0,0,0.05), 0 8px 24px rgba(0,0,0,0.08)",
                            transition: "transform 0.3s ease, box-shadow 0.3s ease",
                        }}
                    >
                        <Box display="flex" alignItems="center" gap={2}>
                            <motion.div whileHover={{ rotate: 15, scale: 1.1 }} whileTap={{ scale: 0.95 }}>
                                <FiFileText style={{ fontSize: 28, color: "#1976d2" }} />
                            </motion.div>
                            <Typography
                                variant="h5"
                                sx={{
                                    fontWeight: "bold",
                                    background: "linear-gradient(90deg, #1976d2, #42a5f5)",
                                    WebkitBackgroundClip: "text",
                                    WebkitTextFillColor: "transparent",
                                    letterSpacing: 0.5,
                                    display: "flex",
                                    alignItems: "center",
                                }}
                            >
                                Questions Table
                            </Typography>

                            {/* Optional: show total questions like a badge */}
                            <Chip
                                label={`${table.getFilteredRowModel().rows.length} Questions`}
                                color="primary"
                                size="small"
                                sx={{ fontWeight: 600 }}
                            />
                        </Box>
                    </Paper>
                </motion.div>


                <Paper elevation={3}>
                    {loading ? (
                        <Box p={3} display="flex" justifyContent="center">
                            <CircularProgress />
                        </Box>
                    ) : (
                        <Box>
                            <TableContainer
                                sx={{
                                    maxHeight: 520,
                                    borderRadius: 2,
                                    overflow: "auto",
                                    '& tbody tr': {
                                        animation: `${rowEnter} .25s ease`,
                                        transition: 'transform .12s ease, box-shadow .12s ease',
                                        backgroundColor: 'rgba(100,181,246,0.1)', // subtle blue highlight
                                    },
                                    '& tbody tr:hover': {
                                        transform: 'scale(1.005)',
                                        boxShadow: '0 6px 14px rgba(0,0,0,.08)',
                                    },
                                    '& tbody tr:nth-of-type(even)': { backgroundColor: 'rgba(25,118,210,0.025)' },
                                }}>

                                <Table stickyHeader>
                                    <TableHead>
                                        {table.getHeaderGroups().map(headerGroup => (
                                            <TableRow key={headerGroup.id} className="table-row-fade">
                                                {headerGroup.headers.map(header => (
                                                    <TableCell key={header.id} sx={{
                                                        background: 'linear-gradient(90deg, #64b5f6 0%, #bbdefb 100%)',
                                                        color: '#fff',
                                                        fontWeight: 700,
                                                    }}>
                                                        {flexRender(header.column.columnDef.header, header.getContext())}

                                                        {header.column.getCanFilter() && (
                                                            <TextField
                                                                size="small"
                                                                variant="outlined"
                                                                placeholder="Filter..."
                                                                value={header.column.getFilterValue() ?? ''}
                                                                onChange={(e) => header.column.setFilterValue(e.target.value)}
                                                                InputProps={{
                                                                    startAdornment: (
                                                                        <InputAdornment position="start">
                                                                            <FiSearch style={{ color: '#1976d2' }} />
                                                                        </InputAdornment>
                                                                    ),
                                                                    sx: { borderRadius: 2, backgroundColor: '#f9f9f9' },
                                                                }}
                                                                sx={{
                                                                    mt: 1,
                                                                    minWidth: 160,
                                                                    '& .MuiOutlinedInput-root': {
                                                                        borderRadius: 2,
                                                                        '& fieldset': { borderColor: 'rgba(25,118,210,0.3)' },
                                                                        '&:hover fieldset': { borderColor: '#1976d2' },
                                                                        '&.Mui-focused fieldset': { borderColor: '#1976d2' },
                                                                    },
                                                                }}
                                                            />
                                                        )}
                                                    </TableCell>
                                                ))}
                                            </TableRow>
                                        ))}
                                    </TableHead>


                                    <TableBody>
                                        {table.getRowModel().rows.length === 0 ? (
                                            <TableRow>
                                                <TableCell
                                                    colSpan={table.getVisibleFlatColumns().length}
                                                    align="center"
                                                    sx={{ py: 5 }}
                                                >
                                                    <Box display="flex" flexDirection="column" alignItems="center" gap={1}>
                                                        <FiAlertCircle size={32} style={{ marginBottom: 4 }} />
                                                        <Typography variant="h6" fontWeight={700}>
                                                            No questions match your filters
                                                        </Typography>
                                                        <Typography variant="body2" color="text.secondary">
                                                            Try adjusting filters or question limit.
                                                        </Typography>
                                                        <Button
                                                            size="small"
                                                            //   variant="contained"
                                                            onClick={() => {
                                                                setSelectedSubject('');
                                                                setSelectedTopic('');
                                                            }}
                                                        >
                                                            Clear Filters
                                                        </Button>
                                                    </Box>
                                                </TableCell>
                                            </TableRow>
                                        ) : (
                                            table.getRowModel().rows.map((row) => (
                                                <TableRow key={row.id} hover className="table-row-fade">
                                                    {row.getVisibleCells().map((cell) => (
                                                        <TableCell key={cell.id}>
                                                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                                        </TableCell>
                                                    ))}
                                                </TableRow>
                                            ))
                                        )}
                                    </TableBody>

                                    <TableFooter
                                        sx={{
                                            position: "sticky",
                                            bottom: 0,
                                            zIndex: 2,
                                            background: "linear-gradient(90deg, #e3f2fd 0%, #bbdefb 100%)",
                                            borderTop: "2px solid #90caf9",
                                            boxShadow: "0 -2px 6px rgba(0,0,0,0.1)",
                                        }}
                                    >
                                        <TableRow>
                                            <TableCell
                                                colSpan={table.getVisibleFlatColumns().length}
                                                align="center"
                                                sx={{
                                                    fontWeight: 600,
                                                    color: "#0d47a1",
                                                    fontSize: "0.95rem",
                                                    letterSpacing: 0.5,
                                                }}
                                            >
                                                📊 Page {table.getState().pagination.pageIndex + 1} of{" "}
                                                {table.getPageCount()} — Showing {table.getState().pagination.pageSize} out of{" "}
                                                {table.getFilteredRowModel().rows.length} questions
                                            </TableCell>
                                        </TableRow>
                                    </TableFooter>
                                </Table>
                            </TableContainer>

                            {/* <Box display="flex" justifyContent="space-between" alignItems="center" p={2}>
                                <Select
                                    size="small"
                                    value={pageSize}
                                    onChange={(e) => table.setPageSize(Number(e.target.value))}
                                >
                                    {[5, 10, 20].map(size => (
                                        <MenuItem key={size} value={size}>
                                            Show {size}
                                        </MenuItem>
                                    ))}
                                </Select>

                                <TablePagination
                                    component="div"
                                    count={table.getFilteredRowModel().rows.length}
                                    page={pageIndex}
                                    onPageChange={(_, newPage) => table.setPageIndex(newPage)}
                                    rowsPerPage={pageSize}
                                    rowsPerPageOptions={[]}
                                />
                            </Box> */}
                            <Stack direction="row" justifyContent="space-between"
                                alignItems="center" p={2} sx={{
                                    background: 'rgba(250,250,250,0.9)',
                                    borderTop: '1px solid rgba(0,0,0,0.05)',
                                    borderRadius: '0 0 12px 12px',
                                }}>
                                <Typography variant="body2" color="text.secondary">
                                    {table.getFilteredRowModel().rows.length} results
                                </Typography>

                                <Stack direction="row" alignItems="center" gap={1}>
                                    <Select size="small" value={pageSize} onChange={(e) => table.setPageSize(Number(e.target.value))}>
                                        {[5, 10, 20].map(size => <MenuItem key={size} value={size}>Show {size}</MenuItem>)}
                                    </Select>

                                    <IconButton disabled={!table.getCanPreviousPage()} onClick={() => table.previousPage()}><FiChevronLeft /></IconButton>
                                    <Typography variant="body2">
                                        Page {pageIndex + 1} / {Math.max(1, table.getPageCount?.() || 1)}
                                    </Typography>
                                    <IconButton disabled={!table.getCanNextPage()} onClick={() => table.nextPage()}><FiChevronRight /></IconButton>
                                </Stack>
                            </Stack>

                        </Box>
                    )}
                </Paper>
            </Paper>




            {/* final section of question set details */}
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
            >
                <Paper
                    elevation={4}
                    sx={{
                        p: 3,
                        mb: 3,
                        borderRadius: 3,
                        //   background: "linear-gradient(145deg, #f9f9f9, #ffffff)",
                        background: "#fff",
                        //   boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
                        boxShadow: "inset 0 1px 3px rgba(0,0,0,0.05), 0 8px 24px rgba(0,0,0,0.08)",
                        transition: "transform 0.3s ease, box-shadow 0.3s ease",
                        "&:hover": {
                            transform: "translateY(-2px) scale(1.01)",
                            boxShadow: "0 16px 36px rgba(0,0,0,0.15)"
                        }
                    }}
                >
                    {/* Heading */}
                    <Box display="flex" alignItems="center" mb={4}>
                        <motion.div whileHover={{ rotate: 15, scale: 1.1 }}>
                            <FiBookOpen style={{ fontSize: 28, color: "#1976d2", marginRight: 8 }} />
                        </motion.div>
                        <Typography
                            variant="h5"
                            sx={{
                                fontWeight: "bold",
                                color: "#1976d2",
                                position: "relative",
                            }}
                        >
                            Question Set Details
                        </Typography>
                    </Box>
                    <Typography variant="subtitle2" color="textSecondary" mb={3}>
                        Enter details to create a new question set.
                    </Typography>
                    <Divider sx={{ mb: 3 }} />


                    {/* Form Fields */}
                    <Grid container spacing={2} alignItems="stretch">
                        {/* Question Set Name */}
                        <Grid item xs={12} md={3} sm={6}>
                            <TextField
                                variant="outlined"
                                fullWidth
                                label={<span>Question Set Name <span style={{ color: 'red' }}>*</span></span>}
                                placeholder="Enter name"
                                value={questionSetName}
                                onChange={(e) => setQuestionSetName(e.target.value)}
                                size="small"
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <FiEdit3 color="#1976d2" />
                                        </InputAdornment>
                                    ),
                                    sx: { borderRadius: 2, backgroundColor: "#fafafa" }
                                }}
                                sx={{
                                    "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                                    {
                                        borderColor: "#1976d2",
                                        boxShadow: "0 0 6px rgba(25, 118, 210, 0.3)"
                                    }
                                }}
                            />
                            <Typography variant="caption" color="textSecondary">
                                {questionSetName.length}/50 characters
                            </Typography>
                        </Grid>

                        {/* Total Marks */}
                        <Grid item xs={12} md={3} sm={6}>
                            <TextField
                                variant="outlined"
                                fullWidth
                                label={<span>Total Marks <span style={{ color: 'red' }}>*</span></span>}
                                placeholder="Enter total marks"
                                type="number"
                                value={totalMarks}
                                onChange={(e) => setTotalMarks(e.target.value)}
                                size="small"
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <FiAward color="#1976d2" />
                                        </InputAdornment>
                                    ),
                                    sx: { borderRadius: 2, backgroundColor: "#fafafa" }
                                }}
                                sx={{
                                    "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                                    {
                                        borderColor: "#1976d2",
                                        boxShadow: "0 0 6px rgba(25, 118, 210, 0.3)"
                                    }
                                }}
                            />
                        </Grid>

                        {/* Passing Marks */}
                        <Grid item xs={12} md={3} sm={6}>
                            <TextField
                                variant="outlined"
                                fullWidth
                                label={<span>Passing Marks <span style={{ color: 'red' }}>*</span></span>}
                                placeholder="Enter passing marks"
                                type="number"
                                value={passingMarks}
                                onChange={(e) => setPassingMarks(e.target.value)}
                                size="small"
                                error={passingError}
                                helperText={passingError ? "Passing marks cannot exceed total marks" : ""}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <FiCheckCircle color="#1976d2" />
                                        </InputAdornment>
                                    ),
                                    sx: { borderRadius: 2, backgroundColor: "#fafafa" }
                                }}
                                sx={{
                                    "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                                    {
                                        borderColor: "#1976d2",
                                        boxShadow: "0 0 6px rgba(25, 118, 210, 0.3)"
                                    }
                                }}
                            />
                        </Grid>

                        {/* Duration */}
                        <Grid item xs={12} md={3} sm={6}>
                            <TextField
                                variant="outlined"
                                fullWidth
                                label="Duration (minutes)"
                                placeholder="Enter duration"
                                type="number"
                                value={duration}
                                onChange={(e) => setDuration(e.target.value)}
                                size="small"
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <FiClock color="#1976d2" />
                                        </InputAdornment>
                                    ),
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <Tooltip title="Duration of the exam in minutes">
                                                <FiInfo color="#1976d2" />
                                            </Tooltip>
                                        </InputAdornment>
                                    ),
                                    sx: { borderRadius: 2, backgroundColor: "#fafafa" }
                                }}
                                sx={{
                                    "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                                    {
                                        borderColor: "#1976d2",
                                        boxShadow: "0 0 6px rgba(25, 118, 210, 0.3)"
                                    }
                                }}
                            />
                        </Grid>

                        {/* Submit Button */}
                        <Grid item xs={12} md="auto" sm={6}>
                            <Tooltip title="Save and create a new question set" arrow>
                                <Button
                                    variant="contained"
                                    fullWidth
                                    onClick={handleCreateQuestionSet}
                                    startIcon={<AiOutlineFileAdd />}
                                    // startIcon={
                                    //     submitting ? (
                                    //         <CircularProgress size={18} color="inherit" />
                                    //     ) : null
                                    // }
                                    sx={{
                                        height: 40,
                                        px: 3,
                                        borderRadius: 2,
                                        background: "linear-gradient(90deg, #1976d2, #42a5f5)",
                                        textTransform: "uppercase",
                                        fontWeight: "bold",
                                        boxShadow: "0 4px 14px rgba(25, 118, 210, 0.4)",
                                        transition: "0.3s",
                                        "&:hover": {
                                            background: "linear-gradient(90deg, #1565c0, #2196f3)",
                                            transform: "translateY(-2px)",
                                            boxShadow: "0 6px 18px rgba(25, 118, 210, 0.6)"
                                        }
                                    }}
                                >
                                    {submitting ? "Submitting..." : "Create Questions Set"}
                                </Button>
                            </Tooltip>
                        </Grid>
                    </Grid>
                </Paper>
            </motion.div>





        </Box>
    );
};

export default QuestionSetPage;
